import landMaskSource from "./land-mask.js?raw";
import { captureBridge } from "@app/capture/bridge.ts";

const RAD = Math.PI / 180;
const MW = 720;
const MH = 360;

const maskBase64 = /"([A-Za-z0-9+/=]+)"/.exec(landMaskSource)?.[1] ?? "";
const rawMask = typeof atob === "function" ? atob(maskBase64) : "";
const mask = new Uint8Array(rawMask.length);
for (let i = 0; i < rawMask.length; i += 1) mask[i] = rawMask.charCodeAt(i);

function isLand(row: number, col: number): boolean {
  let r = row;
  if (r < 0) r = 0;
  if (r >= MH) r = MH - 1;
  const c = ((col % MW) + MW) % MW;
  const k = r * MW + c;
  return ((mask[k >> 3]! >> (7 - (k & 7))) & 1) === 1;
}

export interface LatLon {
  lat: number;
  lon: number;
}

export interface RGB {
  r: number;
  g: number;
  b: number;
  a: number;
}

export interface GlobeTheme {
  ocean: RGB;
  land: RGB;
  coast: RGB;
  grat: RGB;
  rim: string;
  guess: string;
  answer: string;
  glow: number;
  light: boolean;
}

interface Geom {
  w: number;
  h: number;
  cx: number;
  cy: number;
  r: number;
}

export interface GlobeOptions {
  interactive?: boolean;
  /** Densidad máxima de píxeles (el globo decorativo no necesita retina). */
  maxDpr?: number;
  fill?: number;
  onTap?: (p: LatLon) => void;
  onEnter?: () => void;
}

export function prefersReducedMotion(): boolean {
  // El modo captura de reels fuerza movimiento completo.
  if (captureBridge.active) return false;
  return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function readTheme(): GlobeTheme {
  const cs = getComputedStyle(document.documentElement);
  const g = (n: string) => cs.getPropertyValue(n).trim();
  const parseColor = (str: string): RGB => {
    const c = document.createElement("canvas").getContext("2d")!;
    c.fillStyle = "#000";
    c.fillStyle = str.trim();
    const v = c.fillStyle;
    if (v[0] === "#") {
      return {
        r: parseInt(v.slice(1, 3), 16),
        g: parseInt(v.slice(3, 5), 16),
        b: parseInt(v.slice(5, 7), 16),
        a: 1,
      };
    }
    const m = (v.match(/[\d.]+/g) ?? []).map(Number);
    return { r: m[0] ?? 0, g: m[1] ?? 0, b: m[2] ?? 0, a: m[3] ?? 1 };
  };
  return {
    ocean: parseColor(g("--g-ocean")),
    land: parseColor(g("--g-land")),
    coast: parseColor(g("--g-coast")),
    grat: parseColor(g("--g-grat")),
    rim: g("--g-rim"),
    guess: g("--g-guess"),
    answer: g("--g-answer"),
    glow: parseFloat(g("--g-glow")) || 0,
    light:
      document.documentElement.dataset.theme === "light" ||
      (!document.documentElement.dataset.theme && matchMedia("(prefers-color-scheme: light)").matches),
  };
}

export function slerp(a: LatLon, b: LatLon, t: number): LatLon {
  const toVec = (p: LatLon) => {
    const la = p.lat * RAD;
    const lo = p.lon * RAD;
    return [Math.cos(la) * Math.cos(lo), Math.cos(la) * Math.sin(lo), Math.sin(la)] as const;
  };
  const A = toVec(a);
  const B = toVec(b);
  const om = Math.acos(Math.max(-1, Math.min(1, A[0] * B[0] + A[1] * B[1] + A[2] * B[2])));
  if (om < 1e-6) return a;
  const s1 = Math.sin((1 - t) * om) / Math.sin(om);
  const s2 = Math.sin(t * om) / Math.sin(om);
  const v = [A[0] * s1 + B[0] * s2, A[1] * s1 + B[1] * s2, A[2] * s1 + B[2] * s2];
  return {
    lat: Math.asin(Math.max(-1, Math.min(1, v[2]!))) / RAD,
    lon: Math.atan2(v[1]!, v[0]!) / RAD,
  };
}

export class Globe {
  lat0 = 34;
  lon0 = -8;
  zoom = 1;
  guess: LatLon | null = null;
  answer: LatLon | null = null;
  offsetY = 0;
  theme: GlobeTheme = readTheme();

  /** Media resolución (animaciones y arrastres). */
  low = false;
  private dpr = 1;
  private buf: HTMLCanvasElement;
  private zoomTimer: ReturnType<typeof setTimeout> | undefined;
  private frame = 0;
  private observer: ResizeObserver | undefined;

  constructor(
    private canvas: HTMLCanvasElement,
    private opts: GlobeOptions = {},
  ) {
    this.buf = document.createElement("canvas");
    if (typeof ResizeObserver !== "undefined") {
      this.observer = new ResizeObserver(() => this.resize());
      this.observer.observe(canvas.parentElement ?? canvas);
    }
    if (opts.interactive) this.bind();
  }

  destroy(): void {
    this.observer?.disconnect();
    clearTimeout(this.zoomTimer);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  resize(): void {
    const parent = this.canvas.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    this.dpr = Math.min(this.opts.maxDpr ?? 2, window.devicePixelRatio || 1);
    this.canvas.width = Math.round(rect.width * this.dpr);
    this.canvas.height = Math.round(rect.height * this.dpr);
    this.draw();
  }

  private geom(scale: number): Geom {
    const w = Math.round(this.canvas.width * scale);
    const h = Math.round(this.canvas.height * scale);
    const base = Math.min(w, h) * (this.opts.fill ?? 0.44);
    return { w, h, cx: w / 2, cy: h / 2 + this.offsetY * h, r: base * this.zoom };
  }

  /**
   * Pide un redibujado. Varias peticiones en el mismo fotograma (resize, pines,
   * tema…) se agrupan en un solo render: dibujar píxel a píxel es caro.
   */
  draw(): void {
    if (this.frame) return;
    if (typeof requestAnimationFrame !== "function") {
      this.render();
      return;
    }
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.render();
    });
  }

  private render(): void {
    if (!this.canvas.width) return;
    const scale = this.low ? 0.5 : 1;
    const g = this.geom(scale);
    const t = this.theme;
    const ctx2 = this.buf.getContext("2d")!;
    const img = ctx2.createImageData(g.w, g.h);
    const d = img.data;
    const sl = Math.sin(this.lat0 * RAD);
    const cl = Math.cos(this.lat0 * RAD);
    const lon0 = this.lon0;
    const r = g.r;
    const coastW = 0.5 / Math.max(1, this.zoom * 0.8);
    const gw = 0.35 / Math.max(1, this.zoom * 0.7);
    const x0 = Math.max(0, Math.floor(g.cx - r));
    const x1 = Math.min(g.w, Math.ceil(g.cx + r));
    const y0 = Math.max(0, Math.floor(g.cy - r));
    const y1 = Math.min(g.h, Math.ceil(g.cy + r));
    for (let y = y0; y < y1; y += 1) {
      const ny = (g.cy - y) / r;
      for (let x = x0; x < x1; x += 1) {
        const nx = (x - g.cx) / r;
        const p = nx * nx + ny * ny;
        if (p > 1) continue;
        const nz = Math.sqrt(1 - p);
        const lat = Math.asin(ny * cl + nz * sl) / RAD;
        const lon = lon0 + Math.atan2(nx, nz * cl - ny * sl) / RAD;
        const fr = (90 - lat) * 2;
        const fc = (lon + 180) * 2;
        const row = fr | 0;
        const col = Math.floor(fc);
        const L = isLand(row, col);
        const c = L ? t.land : t.ocean;
        let R = c.r;
        let G = c.g;
        let B = c.b;
        const fx = fc - col;
        const fy = fr - row;
        if (
          (fx < coastW && isLand(row, col - 1) !== L) ||
          (fx > 1 - coastW && isLand(row, col + 1) !== L) ||
          (fy < coastW && isLand(row - 1, col) !== L) ||
          (fy > 1 - coastW && isLand(row + 1, col) !== L)
        ) {
          R = t.coast.r;
          G = t.coast.g;
          B = t.coast.b;
        } else {
          const ml = ((lat % 15) + 15) % 15;
          const mo = ((lon % 15) + 15) % 15;
          if (ml < gw || ml > 15 - gw || mo < gw || mo > 15 - gw) {
            const a = t.grat.a;
            R = R * (1 - a) + t.grat.r * a;
            G = G * (1 - a) + t.grat.g * a;
            B = B * (1 - a) + t.grat.b * a;
          }
        }
        const sh = 0.72 + 0.28 * nz;
        const k = (y * g.w + x) * 4;
        d[k] = R * sh;
        d[k + 1] = G * sh;
        d[k + 2] = B * sh;
        d[k + 3] = 255;
      }
    }
    const ctx = this.canvas.getContext("2d")!;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.buf.width = g.w;
    this.buf.height = g.h;
    this.buf.getContext("2d")!.putImageData(img, 0, 0);
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(this.buf, 0, 0, this.canvas.width, this.canvas.height);

    const f = 1 / scale;
    const G2: Geom = { cx: g.cx * f, cy: g.cy * f, r: g.r * f, w: 0, h: 0 };
    ctx.save();
    if (t.glow) {
      ctx.shadowColor = t.rim;
      ctx.shadowBlur = t.glow * this.dpr;
    }
    ctx.strokeStyle = t.rim;
    ctx.lineWidth = 1.5 * this.dpr;
    ctx.beginPath();
    ctx.arc(G2.cx, G2.cy, G2.r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
    if (this.guess && this.answer) this.drawLine(G2);
    if (this.guess) this.drawGuess(G2);
    if (this.answer) this.drawAnswer(G2);
  }

  private project(G: Geom, p: LatLon) {
    const sl = Math.sin(this.lat0 * RAD);
    const cl = Math.cos(this.lat0 * RAD);
    const la = p.lat * RAD;
    const dl = (p.lon - this.lon0) * RAD;
    return {
      x: G.cx + G.r * Math.cos(la) * Math.sin(dl),
      y: G.cy - G.r * (cl * Math.sin(la) - sl * Math.cos(la) * Math.cos(dl)),
      v: sl * Math.sin(la) + cl * Math.cos(la) * Math.cos(dl) > 0,
    };
  }

  unproject(cssX: number, cssY: number): LatLon | null {
    const g = this.geom(1);
    const nx = (cssX * this.dpr - g.cx) / g.r;
    const ny = (g.cy - cssY * this.dpr) / g.r;
    const p = nx * nx + ny * ny;
    if (p > 1) return null;
    const nz = Math.sqrt(1 - p);
    const sl = Math.sin(this.lat0 * RAD);
    const cl = Math.cos(this.lat0 * RAD);
    const lon = this.lon0 + Math.atan2(nx, nz * cl - ny * sl) / RAD;
    return { lat: Math.asin(ny * cl + nz * sl) / RAD, lon: ((lon + 540) % 360) - 180 };
  }

  private drawGuess(G: Geom): void {
    if (!this.guess) return;
    const q = this.project(G, this.guess);
    if (!q.v) return;
    const ctx = this.canvas.getContext("2d")!;
    const u = this.dpr;
    const t = this.theme;
    ctx.save();
    if (t.glow) {
      ctx.shadowColor = t.guess;
      ctx.shadowBlur = t.glow * u;
    }
    ctx.fillStyle = t.guess;
    ctx.strokeStyle = "rgba(0,0,0,.45)";
    ctx.lineWidth = 1.5 * u;
    ctx.beginPath();
    ctx.moveTo(q.x, q.y);
    ctx.bezierCurveTo(q.x - 11 * u, q.y - 14 * u, q.x - 9 * u, q.y - 27 * u, q.x, q.y - 27 * u);
    ctx.bezierCurveTo(q.x + 9 * u, q.y - 27 * u, q.x + 11 * u, q.y - 14 * u, q.x, q.y);
    ctx.fill();
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.fillStyle = "rgba(0,0,0,.55)";
    ctx.beginPath();
    ctx.arc(q.x, q.y - 18 * u, 3.2 * u, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawAnswer(G: Geom): void {
    if (!this.answer) return;
    const q = this.project(G, this.answer);
    if (!q.v) return;
    const ctx = this.canvas.getContext("2d")!;
    const u = this.dpr;
    const t = this.theme;
    const edge = t.light ? "#fff" : "#000";
    ctx.save();
    if (t.glow) {
      ctx.shadowColor = t.answer;
      ctx.shadowBlur = t.glow * u;
    }
    ctx.fillStyle = t.answer;
    ctx.strokeStyle = edge;
    ctx.lineWidth = 2 * u;
    ctx.beginPath();
    ctx.arc(q.x, q.y, 8 * u, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.fillStyle = edge;
    ctx.beginPath();
    ctx.arc(q.x, q.y, 3 * u, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawLine(G: Geom): void {
    if (!this.guess || !this.answer) return;
    const ctx = this.canvas.getContext("2d")!;
    const t = this.theme;
    let pen = false;
    ctx.save();
    ctx.strokeStyle = t.guess;
    ctx.lineWidth = 2.5 * this.dpr;
    ctx.lineCap = "round";
    if (t.glow) {
      ctx.shadowColor = t.guess;
      ctx.shadowBlur = t.glow * this.dpr;
    }
    ctx.beginPath();
    for (let i = 0; i <= 64; i += 1) {
      const q = this.project(G, slerp(this.guess, this.answer, i / 64));
      if (!q.v) {
        pen = false;
        continue;
      }
      if (!pen) {
        ctx.moveTo(q.x, q.y);
        pen = true;
      } else ctx.lineTo(q.x, q.y);
    }
    ctx.stroke();
    ctx.restore();
  }

  setZoom(z: number, fast = false): void {
    this.zoom = Math.max(1, Math.min(14, z));
    if (fast) {
      this.low = true;
      this.draw();
      clearTimeout(this.zoomTimer);
      this.zoomTimer = setTimeout(() => {
        this.low = false;
        this.draw();
      }, 120);
    } else {
      this.draw();
    }
  }

  flyTo(lat: number, lon: number, zoom: number, offsetY: number, done?: () => void): void {
    if (prefersReducedMotion()) {
      this.lat0 = lat;
      this.lon0 = lon;
      this.zoom = zoom;
      this.offsetY = offsetY;
      this.draw();
      done?.();
      return;
    }
    const a = { lat: this.lat0, lon: this.lon0, z: this.zoom, o: this.offsetY };
    const dl = ((lon - a.lon + 540) % 360) - 180;
    const t0 = performance.now();
    const D = 1100;
    const step = (now: number) => {
      const u = Math.min(1, (now - t0) / D);
      const e = u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
      this.lat0 = a.lat + (lat - a.lat) * e;
      this.lon0 = a.lon + dl * e;
      this.zoom = a.z + (zoom - a.z) * e;
      this.offsetY = a.o + (offsetY - a.o) * e;
      this.low = u < 1;
      this.draw();
      if (u < 1) requestAnimationFrame(step);
      else done?.();
    };
    requestAnimationFrame(step);
  }

  private bind(): void {
    const c = this.canvas;
    const ptrs = new Map<number, { x: number; y: number }>();
    let down: { x: number; y: number; lat0: number; lon0: number } | null = null;
    let dragged = false;
    let pinch: { d: number; z: number } | null = null;

    c.addEventListener("pointerdown", (e) => {
      c.setPointerCapture(e.pointerId);
      ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (ptrs.size === 1) {
        down = { x: e.clientX, y: e.clientY, lat0: this.lat0, lon0: this.lon0 };
        dragged = false;
      }
      if (ptrs.size === 2) {
        const v = [...ptrs.values()];
        pinch = { d: Math.hypot(v[0]!.x - v[1]!.x, v[0]!.y - v[1]!.y), z: this.zoom };
        dragged = true;
      }
    });
    c.addEventListener("pointermove", (e) => {
      if (!ptrs.has(e.pointerId)) return;
      ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pinch && ptrs.size === 2) {
        const v = [...ptrs.values()];
        this.setZoom(
          (pinch.z * Math.hypot(v[0]!.x - v[1]!.x, v[0]!.y - v[1]!.y)) / pinch.d,
          true,
        );
        return;
      }
      if (!down) return;
      const dx = e.clientX - down.x;
      const dy = e.clientY - down.y;
      if (!dragged && Math.hypot(dx, dy) > 6) dragged = true;
      if (dragged) {
        const f = 180 / Math.PI / (this.geom(1).r / this.dpr);
        this.lon0 = down.lon0 - dx * f;
        this.lat0 = Math.max(-80, Math.min(80, down.lat0 + dy * f));
        this.low = true;
        this.draw();
      }
    });
    const end = (e: PointerEvent) => {
      if (!ptrs.has(e.pointerId)) return;
      ptrs.delete(e.pointerId);
      if (ptrs.size < 2) pinch = null;
      if (ptrs.size === 0) {
        if (!dragged && down && this.opts.onTap) {
          const rc = c.getBoundingClientRect();
          const p = this.unproject(e.clientX - rc.left, e.clientY - rc.top);
          if (p) this.opts.onTap(p);
        }
        down = null;
        this.low = false;
        this.draw();
      }
    };
    c.addEventListener("pointerup", end);
    c.addEventListener("pointercancel", end);
    c.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        this.setZoom(this.zoom * (e.deltaY < 0 ? 1.15 : 1 / 1.15), true);
      },
      { passive: false },
    );
    c.addEventListener("keydown", (e) => {
      const st = 6 / this.zoom;
      switch (e.key) {
        case "ArrowLeft":
          this.lon0 -= st;
          break;
        case "ArrowRight":
          this.lon0 += st;
          break;
        case "ArrowUp":
          this.lat0 = Math.min(80, this.lat0 + st);
          break;
        case "ArrowDown":
          this.lat0 = Math.max(-80, this.lat0 - st);
          break;
        case "+":
        case "=":
          this.setZoom(this.zoom * 1.3);
          e.preventDefault();
          return;
        case "-":
          this.setZoom(this.zoom / 1.3);
          e.preventDefault();
          return;
        case " ":
          this.opts.onTap?.({ lat: this.lat0, lon: ((this.lon0 + 540) % 360) - 180 });
          e.preventDefault();
          return;
        case "Enter":
          this.opts.onEnter?.();
          return;
        default:
          return;
      }
      e.preventDefault();
      this.draw();
    });
  }
}
